/**
 * MOCK/TEMPORARY — playlists.
 *
 * Playlists live in the client-side playlistsStore; this module is the seam that
 * store writes through, exactly as songsStore writes through songsApi. Nothing here
 * persists across a reload, which is the current, expected behaviour and is the same
 * for songs, credits and the player.
 *
 * A playlist is the one entity in the app that is purely the user's own data — no
 * generation, no model, no audio — so unlike Studio actions there is nothing here
 * that a backend cannot straightforwardly implement. Every function below is a
 * one-line rewrite into `fetch`, and the store above it does not change.
 *
 * BACKEND DEPENDENCY. Until these endpoints exist, a playlist is lost on reload.
 * That is the only thing missing: the feature itself is real, in that it does
 * exactly what it says to real songs the user owns.
 *
 * Real endpoints: docs/api-contract.md, "Playlists".
 */
import { USE_MOCK_API } from '../config';
import type { Playlist, Song } from '../types/domain';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

export const playlistsApi = {
  /**
   * MOCK: GET /playlists — returns the empty set.
   *
   * Deliberately empty rather than seeded with fixtures. The seed library exists
   * because the app has to have songs to show; inventing playlists the user never
   * made would put fabricated content in a list whose entire purpose is to hold
   * what they chose, and the empty state is a designed screen.
   */
  async getPlaylists(): Promise<Playlist[]> {
    if (!USE_MOCK_API) {
      return http.get<Playlist[]>('/playlists');
    }
    assertMock('playlistsApi.getPlaylists');
    return mockDelay([]);
  },

  /** MOCK: POST /playlists */
  async createPlaylist(name: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<Playlist>('/playlists', { name });
      return;
    }
    assertMock('playlistsApi.createPlaylist');
    return mockDelay(undefined);
  },

  /** MOCK: PATCH /playlists/:id */
  async renamePlaylist(id: string, name: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.patch<void>(`/playlists/${id}`, { name });
      return;
    }
    assertMock('playlistsApi.renamePlaylist');
    return mockDelay(undefined);
  },

  /** MOCK: DELETE /playlists/:id */
  async deletePlaylist(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/playlists/${id}`);
      return;
    }
    assertMock('playlistsApi.deletePlaylist');
    return mockDelay(undefined);
  },

  /** MOCK: POST /playlists/:id/songs */
  async addSong(playlistId: string, songId: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>(`/playlists/${playlistId}/songs`, { songId });
      return;
    }
    assertMock('playlistsApi.addSong');
    return mockDelay(undefined);
  },

  /** GET /playlists/songs — every song referenced by the user's playlists. */
  async getPlaylistSongs(): Promise<Song[]> {
    if (!USE_MOCK_API) {
      return http.get<Song[]>('/playlists/songs');
    }
    assertMock('playlistsApi.getPlaylistSongs');
    return mockDelay([]);
  },

  /** MOCK: DELETE /playlists/:playlistId/songs/:songId */
  async removeSong(playlistId: string, songId: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>(`/playlists/${playlistId}/songs/${songId}`, { method: 'DELETE' });
      return;
    }
    assertMock('playlistsApi.removeSong');
    return mockDelay(undefined);
  },

  /**
   * MOCK: PUT /playlists/:id/songs
   *
   * The whole ordered list rather than a move-from/move-to pair. Reordering is
   * idempotent that way: a client that retries after a dropped response cannot
   * apply the same move twice, which a relative "move up" endpoint would.
   */
  async reorderSongs(playlistId: string, songIds: string[]): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>(`/playlists/${playlistId}/songs`, {
        method: 'PUT',
        body: { songIds },
      });
      return;
    }
    assertMock('playlistsApi.reorderSongs');
    return mockDelay(undefined);
  },
};
